import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = [
    "title", "titleText", "icon", "description",
    "petSelectionLabel", "petList", "dateTrack",
    "optionsPanel", "optionsPanelContent", "topBorderLeft", "topBorderGap", "topBorderRight",
    "partialPetWarning", "partialPetWarningText",
    "timeSelection", "timeSlots", "notesSection", "notes",
    "priceDisplay", "confirmButton",
    "activityNotes", "activityNotesLabel", "activityNotesError"
  ]

  static values = {
    orderId: String,
    orderItemId: String
  }

  connect() {
    this.activity = null
    this.pets = []
    this.dates = []
    this.selectedPets = new Set()
    this.selectedDates = new Map() // date -> { selected: bool, times: string[], notes: string }
    this.currentExpandedDate = null
    this.basePrice = 0
    this.activityLevelNotes = '' // Activity-level special instructions (applies to all sessions)
  }

  // Called externally to open modal with activity data
  open(activityData, pets, dates, existingSelections = []) {
    this.activity = activityData
    this.pets = pets
    // Filter dates based on day availability (Saturday/Sunday restrictions)
    this.dates = this.filterAvailableDates(dates)
    this.basePrice = parseFloat(activityData.price) || 0
    this.existingSelections = existingSelections

    // Reset selections
    this.selectedDates = new Map()
    this.currentExpandedDate = null

    // Load activity-level notes from activity data (stored at OrderItem level)
    this.activityLevelNotes = activityData.activity_notes || ''
    if (this.hasActivityNotesTarget) {
      this.activityNotesTarget.value = this.activityLevelNotes
    }

    // Medication-grouped activities: rename label, drop "(optional)", require the field.
    this.applyMedicationConfig(activityData.is_medication === true)

    // Load per-date notes
    this.dateNotes = activityData.date_notes || {}

    // Apply existing selections if updating
    if (existingSelections.length > 0) {
      this.applyExistingSelections(existingSelections, this.dateNotes)
    } else {
      // New activity - select only applicable pets by default (based on pet_type restriction)
      const applicablePets = pets.filter(p => this.isPetApplicableForActivity(p))
      this.selectedPets = new Set(applicablePets.map(p => p.id))

      // Don't auto-select dates/times - user must explicitly choose
    }

    // Populate modal
    this.populateHeader()
    this.populatePetDropdown()
    this.populateDateCarousel()
    this.applyDateCardStyles()
    this.hideOptionsPanel()
    this.updateSummary()
    this.updateConfirmButton()

    // Show modal
    this.element.classList.remove('hidden')
    document.body.classList.add('overflow-hidden')
  }

  // Apply existing selections when updating an activity
  applyExistingSelections(selections, dateNotes = {}) {
    // Select only applicable pets by default when updating (based on pet_type restriction)
    const applicablePets = this.pets.filter(p => this.isPetApplicableForActivity(p))
    this.selectedPets = new Set(applicablePets.map(p => p.id))

    // Store raw selections for reference when filtering by pet
    this.existingSelections = selections

    // Group selections by date, tracking which pets have which times
    const selectionsByDate = {}
    selections.forEach(s => {
      if (!selectionsByDate[s.date]) {
        selectionsByDate[s.date] = { times: [], petPpIds: new Set(), timesByPetPpId: {} }
      }
      if (s.time && !selectionsByDate[s.date].times.includes(s.time)) {
        selectionsByDate[s.date].times.push(s.time)
      }
      if (s.pp_pet_id) {
        selectionsByDate[s.date].petPpIds.add(String(s.pp_pet_id))
        // Track times per pet
        const petPpId = String(s.pp_pet_id)
        if (!selectionsByDate[s.date].timesByPetPpId[petPpId]) {
          selectionsByDate[s.date].timesByPetPpId[petPpId] = []
        }
        if (s.time && !selectionsByDate[s.date].timesByPetPpId[petPpId].includes(s.time)) {
          selectionsByDate[s.date].timesByPetPpId[petPpId].push(s.time)
        }
      }
    })

    // Apply to selectedDates map
    Object.entries(selectionsByDate).forEach(([date, data]) => {
      // Convert petPpIds Set to array of pet prefix_ids
      const petsWithSelections = this.pets
        .filter(p => data.petPpIds.has(String(p.pp_pet_id)))
        .map(p => p.id)

      // Build timesByPetId using prefix_ids instead of pp_pet_ids
      const timesByPetId = {}
      this.pets.forEach(pet => {
        const petPpId = String(pet.pp_pet_id)
        if (data.timesByPetPpId[petPpId]) {
          timesByPetId[pet.id] = data.timesByPetPpId[petPpId]
        }
      })

      // Get per-date notes from the dateNotes object
      const notes = dateNotes[date] || ''

      this.selectedDates.set(date, {
        selected: true,
        times: data.times,
        notes: notes,
        petIds: petsWithSelections, // Lock in the pets for this date (used by confirm)
        existingPetIds: petsWithSelections, // Track which pets had existing selections (used for styling)
        timesByPetId: timesByPetId // Track times per pet for filtering
      })
    })
  }

  // Auto-select all dates with first available time slot (for new activities via Add button)
  autoSelectAllDatesWithMorning() {
    const currentPetIds = Array.from(this.selectedPets)
    const firstTime = this.getFirstAvailableTime()

    // Build timesByPetId - each pet gets first available time for all dates
    this.dates.forEach(date => {
      const timesByPetId = {}
      currentPetIds.forEach(petId => {
        timesByPetId[petId] = [firstTime]
      })

      this.selectedDates.set(date, {
        selected: true,
        times: [firstTime],
        notes: '',
        petIds: currentPetIds,
        timesByPetId: timesByPetId
      })
    })
  }

  // Apply styles to date cards based on current selections
  applyDateCardStyles() {
    if (!this.hasDateTrackTarget) return

    this.selectedDates.forEach((data, date) => {
      if (data.selected) {
        const dateCard = this.dateTrackTarget.querySelector(`.date-card[data-date="${date}"]`)
        if (dateCard) {
          const petIdsForDate = data.petIds || data.existingPetIds || []
          const { isPartial, missingPets, hasCurrentPet } = this.getPartialSelectionInfo(petIdsForDate, data)

          // Only show as selected if the current pet(s) have this date
          this.updateDateCardStyle(dateCard, hasCurrentPet, isPartial)

          // Add warning message for partial selections (only when both pets selected)
          if (isPartial && missingPets.length > 0) {
            this.addPartialSelectionWarning(dateCard, petIdsForDate, missingPets)
          } else {
            this.removePartialSelectionWarning(dateCard)
          }
        }
      }
    })
  }

  // Determine if a date has partial pet coverage based on currently selected pets
  // Returns: { isPartial, missingPets, hasCurrentPet }
  getPartialSelectionInfo(petIdsForDate, dateData = null) {
    // Check if any of the currently selected pets have times for this date
    const hasAnyTimesForCurrentPets = this.hasAnyTimesForCurrentPets(dateData)

    // If only one pet is selected in the dropdown
    if (this.selectedPets.size <= 1) {
      const selectedPetId = Array.from(this.selectedPets)[0]
      const hasSelectedPet = petIdsForDate.includes(selectedPetId) && hasAnyTimesForCurrentPets
      // No partial warning for single pet, but track if they have this date
      return { isPartial: false, missingPets: [], hasCurrentPet: hasSelectedPet }
    }

    // If no times selected for any current pet, not selected
    if (!hasAnyTimesForCurrentPets) {
      return { isPartial: false, missingPets: [], hasCurrentPet: false }
    }

    // Check if any time slot has partial pet coverage
    if (dateData?.timesByPetId) {
      const hasPartialTime = this.hasAnyPartialTime(dateData.timesByPetId)
      if (hasPartialTime) {
        const missingPets = Array.from(this.selectedPets).filter(petId => !petIdsForDate.includes(petId))
        return { isPartial: true, missingPets, hasCurrentPet: true }
      }
    }

    // Multiple pets selected - check which of the selected pets are missing from this date
    const missingPets = Array.from(this.selectedPets).filter(petId => !petIdsForDate.includes(petId))
    const isPartial = missingPets.length > 0 && missingPets.length < this.selectedPets.size

    return { isPartial, missingPets, hasCurrentPet: true }
  }

  // Check if any currently selected pet has times for this date
  hasAnyTimesForCurrentPets(dateData) {
    if (!dateData?.timesByPetId) return false

    for (const petId of this.selectedPets) {
      const times = dateData.timesByPetId[petId]
      if (times && times.length > 0) {
        return true
      }
    }
    return false
  }

  // Check if any time slot has partial pet coverage (not all selected pets have it)
  hasAnyPartialTime(timesByPetId) {
    if (!timesByPetId || this.selectedPets.size <= 1) return false

    // Build a map of time -> which selected pets have it
    const timesByTime = {}
    Object.entries(timesByPetId).forEach(([petId, times]) => {
      if (!this.selectedPets.has(petId)) return
      times.forEach(time => {
        if (!timesByTime[time]) timesByTime[time] = new Set()
        timesByTime[time].add(petId)
      })
    })

    // Check if any time has fewer pets than the total selected
    for (const [time, petIds] of Object.entries(timesByTime)) {
      if (petIds.size < this.selectedPets.size) {
        return true
      }
    }
    return false
  }

  // Store partial selection info for the current expanded date (displayed in options panel)
  addPartialSelectionWarning(dateCard, petIdsForDate, missingPets) {
    // This is now handled by updatePartialWarningInPanel when expanding a date
    // Keep this method for compatibility but it no longer adds anything to the card
  }

  // Refresh all date card styles (called when pet selection changes)
  refreshDateCardStyles() {
    if (!this.hasDateTrackTarget) return

    this.selectedDates.forEach((data, date) => {
      const dateCard = this.dateTrackTarget.querySelector(`.date-card[data-date="${date}"]`)
      if (!dateCard) return

      if (data.selected) {
        const petIdsForDate = data.petIds || data.existingPetIds || []
        const { isPartial, missingPets, hasCurrentPet } = this.getPartialSelectionInfo(petIdsForDate, data)

        // Only show as selected if the current pet(s) have this date
        this.updateDateCardStyle(dateCard, hasCurrentPet, isPartial)

        if (isPartial && missingPets.length > 0) {
          this.addPartialSelectionWarning(dateCard, petIdsForDate, missingPets)
        } else {
          this.removePartialSelectionWarning(dateCard)
        }
      } else {
        this.updateDateCardStyle(dateCard, false)
        this.removePartialSelectionWarning(dateCard)
      }
    })
  }

  // Remove partial selection warning (no longer on card, handled in panel)
  removePartialSelectionWarning(dateCard) {
    // No longer adding warnings to cards
  }

  // Update the partial pet warning in the options panel (only shown when multiple pets selected)
  updatePartialWarningInPanel(date) {
    if (!this.hasPartialPetWarningTarget || !this.hasPartialPetWarningTextTarget) return

    // Only show warning when multiple pets are selected
    if (this.selectedPets.size <= 1) {
      this.partialPetWarningTarget.classList.add('hidden')
      return
    }

    const dateData = this.selectedDates.get(date)
    if (!dateData?.selected || !dateData.timesByPetId) {
      this.partialPetWarningTarget.classList.add('hidden')
      return
    }

    // Build a summary of which pets have which times
    // Group by time to find shared times
    const timesByTime = {}
    Object.entries(dateData.timesByPetId).forEach(([petId, times]) => {
      const pet = this.pets.find(p => p.id === petId)
      if (!pet || !this.selectedPets.has(petId)) return

      times.forEach(time => {
        if (!timesByTime[time]) timesByTime[time] = []
        timesByTime[time].push(pet.name)
      })
    })

    // Check if any time has partial pet coverage (amber button)
    let hasPartialTime = false
    const summaryParts = []

    Object.entries(timesByTime).forEach(([time, petNames]) => {
      const timeLabel = this.getTimeLabel(time)
      if (petNames.length === this.selectedPets.size) {
        summaryParts.push(`${timeLabel}: All pets`)
      } else {
        hasPartialTime = true
        summaryParts.push(`${timeLabel}: ${petNames.join(' & ')}`)
      }
    })

    // Only show warning if there's at least one partial (amber) time
    if (hasPartialTime && summaryParts.length > 0) {
      this.partialPetWarningTextTarget.textContent = summaryParts.join(' · ')
      this.partialPetWarningTarget.classList.remove('hidden')
    } else {
      this.partialPetWarningTarget.classList.add('hidden')
    }
  }

  hide(event) {
    if (event) event.preventDefault()

    // Save current date's notes and trigger final save before closing
    this.saveCurrentNotes()

    // Clear any pending auto-save and do immediate save
    if (this.autoSaveTimeout) {
      clearTimeout(this.autoSaveTimeout)
    }

    const selections = this.buildSelections()

    // Block save when a medication activity has been configured but no
    // medication details have been entered. Cancelling out without any
    // selections is still allowed.
    if (this.activity?.is_medication && selections.length > 0 && this.activityLevelNotes.trim() === '') {
      this.showActivityNotesError()
      return
    }
    this.hideActivityNotesError()

    this.dispatch('confirmed', {
      detail: {
        activity: this.activity,
        selections: selections,
        activityNotes: this.activityLevelNotes
      }
    })

    this.element.classList.add('hidden')
    document.body.classList.remove('overflow-hidden')
  }

  applyMedicationConfig(isMedication) {
    if (!this.hasActivityNotesTarget) return

    if (isMedication) {
      if (this.hasActivityNotesLabelTarget) {
        this.activityNotesLabelTarget.textContent = 'Medication details'
      }
      this.activityNotesTarget.required = true
      this.activityNotesTarget.placeholder = 'Drug name, dose, frequency, with/without food, etc.'
    } else {
      if (this.hasActivityNotesLabelTarget) {
        this.activityNotesLabelTarget.innerHTML = 'Special Instructions <span class="font-normal text-gray-500">(optional)</span>'
      }
      this.activityNotesTarget.required = false
      this.activityNotesTarget.placeholder = 'Any special instructions for this activity...'
    }
    this.hideActivityNotesError()
  }

  showActivityNotesError() {
    if (this.hasActivityNotesErrorTarget) {
      this.activityNotesErrorTarget.classList.remove('hidden')
    }
    if (this.hasActivityNotesTarget) {
      this.activityNotesTarget.classList.add('border-red-500', 'ring-2', 'ring-red-200')
      this.activityNotesTarget.focus()
    }
  }

  hideActivityNotesError() {
    if (this.hasActivityNotesErrorTarget) {
      this.activityNotesErrorTarget.classList.add('hidden')
    }
    if (this.hasActivityNotesTarget) {
      this.activityNotesTarget.classList.remove('border-red-500', 'ring-2', 'ring-red-200')
    }
  }

  hideOnBackdrop(event) {
    if (event.target === this.element) {
      this.hide(event)
    }
  }

  populateHeader() {
    if (this.hasTitleTextTarget) {
      this.titleTextTarget.textContent = this.activity.title
    }
    if (this.hasIconTarget && this.activity.icon) {
      this.iconTarget.className = `${this.activity.icon} mr-2 text-[var(--color-primary)]`
    }
    if (this.hasDescriptionTarget) {
      const rawHtml = this.activity.description_html || this.activity.description || ''
      this.descriptionTarget.innerHTML = this.decodeHtmlEntities(rawHtml)
    }
  }

  // Decode HTML entities (handles double-encoded content from legacy systems)
  decodeHtmlEntities(html) {
    const textarea = document.createElement('textarea')
    textarea.innerHTML = html
    return textarea.value
  }

  populatePetDropdown() {
    if (!this.hasPetListTarget) return

    const html = this.pets.map(pet => {
      const isApplicable = this.isPetApplicableForActivity(pet)
      const disabledClass = isApplicable ? '' : 'opacity-50 cursor-not-allowed'
      const hoverClass = isApplicable ? 'hover:bg-gray-50' : ''
      const textClass = isApplicable ? 'text-gray-700' : 'text-gray-400'

      return `
        <label class="flex items-center p-2 ${hoverClass} rounded-lg ${isApplicable ? 'cursor-pointer' : 'cursor-not-allowed'} ${disabledClass}">
          <input type="checkbox"
            class="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500 ${isApplicable ? '' : 'cursor-not-allowed'}"
            data-pet-id="${pet.id}"
            data-action="change->activity-modal#togglePet"
            ${this.selectedPets.has(pet.id) ? 'checked' : ''}
            ${isApplicable ? '' : 'disabled'}>
          <span class="ml-3 ${textClass}">${pet.name}</span>
          ${!isApplicable ? `<span class="ml-2 text-xs text-gray-400">(${this.getActivityPetTypeName()} only)</span>` : ''}
        </label>
      `
    }).join('')

    this.petListTarget.innerHTML = html
    this.updatePetSelectionLabel()
  }

  // Check if a pet is applicable for the current activity based on pet_type restriction
  isPetApplicableForActivity(pet) {
    const activityPetType = this.activity?.pet_type
    if (!activityPetType) return true // No restriction, all pets are applicable

    // Map activity pet_type (dog, cat, other) to pet species (D, C, O)
    const petTypeToSpecies = {
      'dog': 'D',
      'cat': 'C',
      'other': 'O'
    }

    const requiredSpecies = petTypeToSpecies[activityPetType]
    return pet.species === requiredSpecies
  }

  // Get the human-readable name for the activity's pet_type restriction
  getActivityPetTypeName() {
    const activityPetType = this.activity?.pet_type
    const names = {
      'dog': 'Dogs',
      'cat': 'Cats',
      'other': 'Other pets'
    }
    return names[activityPetType] || activityPetType
  }

  togglePet(event) {
    const petId = event.target.dataset.petId
    if (event.target.checked) {
      this.selectedPets.add(petId)
    } else {
      this.selectedPets.delete(petId)
    }
    this.updatePetSelectionLabel()
    this.refreshDateCardStyles() // Update styles based on new pet selection

    // Re-render time slots and update panel border if a date is expanded
    if (this.currentExpandedDate) {
      this.populateTimeSlots(this.currentExpandedDate)
      this.updatePartialWarningInPanel(this.currentExpandedDate)

      // Update options panel border color based on new pet selection
      const dateData = this.selectedDates.get(this.currentExpandedDate)
      const petIdsForDate = dateData?.petIds || dateData?.existingPetIds || []
      const { isPartial, hasCurrentPet } = this.getPartialSelectionInfo(petIdsForDate, dateData)
      this.updateOptionsPanelBorder(hasCurrentPet, isPartial)
    }

    this.updateSummary()
    this.updateConfirmButton()
  }

  updatePetSelectionLabel() {
    if (!this.hasPetSelectionLabelTarget) return

    const selectedPetNames = this.pets
      .filter(p => this.selectedPets.has(p.id))
      .map(p => p.name)

    let labelText
    if (selectedPetNames.length === 0) {
      labelText = 'No pets selected'
    } else if (selectedPetNames.length === 1) {
      labelText = `${selectedPetNames[0]} only`
    } else {
      labelText = this.formatPetNames(selectedPetNames)
    }

    this.petSelectionLabelTarget.innerHTML = `
      <i class="fa-solid fa-paw text-gray-400 mr-2"></i>
      ${labelText}
    `
  }

  // Formats pet names: "Max", "Max & Bella", "Max, Bella & Charlie"
  formatPetNames(names) {
    if (names.length === 2) {
      return `${names[0]} & ${names[1]}`
    }
    const allButLast = names.slice(0, -1).join(', ')
    const last = names[names.length - 1]
    return `${allButLast} & ${last}`
  }

  populateDateCarousel() {
    if (!this.hasDateTrackTarget) return

    const html = this.dates.map((date, index) => {
      const dateObj = this.parseLocalDate(date)
      const dayName = dateObj.toLocaleDateString('en-US', { weekday: 'short' })
      const dayNum = dateObj.getDate()
      const monthName = dateObj.toLocaleDateString('en-US', { month: 'short' })

      // Negative margin on all but first to overlap borders seamlessly
      const marginClass = index === 0 ? '' : '-ml-[2px]'

      return `
        <div class="date-card-wrapper flex-shrink-0 w-20 ${marginClass} relative z-10">
          <div class="date-card border-2 border-gray-200 p-3 text-center transition-all hover:border-gray-300 bg-white select-none"
            data-activity-modal-target="dateCard"
            data-date="${date}"
            data-action="click->activity-modal#expandDate">
            <div class="flex items-center justify-center mb-1">
              <div class="w-5 h-5 border-2 border-gray-300 rounded flex items-center justify-center transition-colors date-checkbox cursor-pointer hover:border-gray-400"
                data-action="click->activity-modal#toggleDateSelection:stop">
                <i class="fa-solid fa-check text-white text-xs hidden"></i>
              </div>
            </div>
            <div class="text-xs text-gray-500 uppercase font-medium">${dayName}</div>
            <div class="text-lg font-bold text-gray-900">${dayNum}</div>
            <div class="text-xs text-gray-500">${monthName}</div>
          </div>
        </div>
      `
    }).join('')

    this.dateTrackTarget.innerHTML = html
  }

  // Clicking the checkbox toggles selection
  toggleDateSelection(event) {
    event.preventDefault()
    event.stopPropagation()

    const dateCard = event.target.closest('.date-card')
    if (!dateCard) return

    const date = dateCard.dataset.date
    const dateData = this.selectedDates.get(date)
    const currentPetIds = Array.from(this.selectedPets)
    const existingPetIds = dateData?.petIds || []

    // Check if all currently selected pets already have this date
    const allCurrentPetsHaveDate = currentPetIds.every(petId => existingPetIds.includes(petId))

    if (dateData?.selected && allCurrentPetsHaveDate) {
      // All selected pets already have this date - deselect them
      // Remove only the currently selected pets from this date
      const remainingPetIds = existingPetIds.filter(petId => !currentPetIds.includes(petId))
      const remainingTimesByPetId = { ...(dateData.timesByPetId || {}) }

      // Remove times for deselected pets
      currentPetIds.forEach(petId => {
        delete remainingTimesByPetId[petId]
      })

      if (remainingPetIds.length === 0) {
        // No pets left - fully deselect
        this.selectedDates.set(date, { selected: false, times: [], notes: '', petIds: [], timesByPetId: {} })

        // Remove tab connection styling and force gray/unselected state
        dateCard.classList.remove('border-b-0')
        dateCard.classList.remove('border-green-500', 'bg-green-50', 'border-amber-500', 'bg-amber-50')
        dateCard.classList.add('border-gray-200', 'bg-white')

        // Update wrapper z-index
        const wrapper = dateCard.closest('.date-card-wrapper')
        if (wrapper) {
          wrapper.classList.remove('z-20')
          wrapper.classList.add('z-10')
        }

        // Update checkbox
        const checkbox = dateCard.querySelector('.date-checkbox')
        const checkIcon = checkbox?.querySelector('i')
        if (checkbox) {
          checkbox.classList.remove('border-green-500', 'bg-green-500', 'border-amber-500', 'bg-amber-500')
          checkbox.classList.add('border-gray-300')
        }
        if (checkIcon) {
          checkIcon.classList.add('hidden')
        }

        // Refresh time slots and panel to show unselected state
        this.populateTimeSlots(date)
        this.updatePartialWarningInPanel(date)

        if (this.currentExpandedDate === date) {
          this.updateOptionsPanelBorder(false)
        }
      } else {
        // Some pets remain - update with remaining
        const remainingTimes = new Set()
        Object.values(remainingTimesByPetId).forEach(times => {
          times.forEach(t => remainingTimes.add(t))
        })

        const newDateData = {
          selected: true,
          times: Array.from(remainingTimes),
          notes: dateData.notes || '',
          petIds: remainingPetIds,
          timesByPetId: remainingTimesByPetId
        }
        this.selectedDates.set(date, newDateData)

        // Update card style based on whether current pet still has this date
        const { isPartial, hasCurrentPet } = this.getPartialSelectionInfo(remainingPetIds, newDateData)
        if (hasCurrentPet) {
          this.updateDateCardStyle(dateCard, true, isPartial)
        } else {
          // Current pet no longer has this date - show as unselected for them
          dateCard.classList.remove('border-b-0')
          dateCard.classList.remove('border-green-500', 'bg-green-50', 'border-amber-500', 'bg-amber-50')
          dateCard.classList.add('border-gray-200', 'bg-white')

          // Update wrapper z-index
          const wrapperEl = dateCard.closest('.date-card-wrapper')
          if (wrapperEl) {
            wrapperEl.classList.remove('z-20')
            wrapperEl.classList.add('z-10')
          }

          const checkbox = dateCard.querySelector('.date-checkbox')
          const checkIcon = checkbox?.querySelector('i')
          if (checkbox) {
            checkbox.classList.remove('border-green-500', 'bg-green-500', 'border-amber-500', 'bg-amber-500')
            checkbox.classList.add('border-gray-300')
          }
          if (checkIcon) {
            checkIcon.classList.add('hidden')
          }
        }

        // Refresh time slots and panel
        this.populateTimeSlots(date)
        this.updatePartialWarningInPanel(date)
        this.updateOptionsPanelBorder(hasCurrentPet, isPartial)
      }
    } else {
      // Add the currently selected pets to this date
      const existingTimesByPetId = dateData?.timesByPetId || {}
      const timesByPetId = { ...existingTimesByPetId }

      // Merge existing petIds with current selection
      const mergedPetIds = [...new Set([...existingPetIds, ...currentPetIds])]
      const defaultTime = this.getFirstAvailableTime()

      // Add first available time only for newly selected pets that don't have any times
      currentPetIds.forEach(petId => {
        if (!timesByPetId[petId] || timesByPetId[petId].length === 0) {
          timesByPetId[petId] = [defaultTime]
        }
      })

      // Collect all times across all pets
      const allTimes = new Set()
      Object.values(timesByPetId).forEach(times => {
        times.forEach(t => allTimes.add(t))
      })

      const newDateData = {
        selected: true,
        times: Array.from(allTimes),
        notes: dateData?.notes || '',
        petIds: mergedPetIds,
        timesByPetId: timesByPetId
      }
      this.selectedDates.set(date, newDateData)

      // Update card style with partial info
      const { isPartial } = this.getPartialSelectionInfo(mergedPetIds, newDateData)
      this.updateDateCardStyle(dateCard, true, isPartial)

      // Expand options for this date
      this.expandDateOptions(date)
    }

    this.updateSummary()
    this.autoSave()
  }

  // Clicking the card expands options (without changing selection)
  expandDate(event) {
    const dateCard = event.currentTarget
    if (!dateCard) return

    const date = dateCard.dataset.date
    this.expandDateOptions(date)
  }

  updateDateCardStyle(card, selected, isPartial = false) {
    const checkbox = card.querySelector('.date-checkbox')
    const checkIcon = checkbox?.querySelector('i')
    const wrapper = card.closest('.date-card-wrapper')

    // Remove all state classes first
    card.classList.remove('border-gray-200', 'bg-white', 'border-green-500', 'bg-green-50', 'border-amber-500', 'bg-amber-50')
    if (wrapper) {
      wrapper.classList.remove('z-10', 'z-20')
    }
    if (checkbox) {
      checkbox.classList.remove('border-gray-300', 'border-green-500', 'bg-green-500', 'border-amber-500', 'bg-amber-500')
    }

    if (selected) {
      // Raise wrapper z-index so selected card's border overlaps adjacent cards
      if (wrapper) {
        wrapper.classList.add('z-20')
      }
      if (isPartial) {
        // Partial selection - amber/yellow styling
        card.classList.add('border-amber-500', 'bg-amber-50')
        if (checkbox) {
          checkbox.classList.add('border-amber-500', 'bg-amber-500')
        }
      } else {
        // Full selection - green styling
        card.classList.add('border-green-500', 'bg-green-50')
        if (checkbox) {
          checkbox.classList.add('border-green-500', 'bg-green-500')
        }
      }
      if (checkIcon) {
        checkIcon.classList.remove('hidden')
      }
    } else {
      // Not selected - default styling
      card.classList.add('border-gray-200', 'bg-white')
      if (wrapper) {
        wrapper.classList.add('z-10')
      }
      if (checkbox) {
        checkbox.classList.add('border-gray-300')
      }
      if (checkIcon) {
        checkIcon.classList.add('hidden')
      }
      // Remove any partial warning when deselecting
      this.removePartialSelectionWarning(card)
    }
  }

  expandDateOptions(date) {
    // Save notes from previously expanded date before switching
    this.saveCurrentNotes()

    this.currentExpandedDate = date

    if (!this.hasOptionsPanelTarget) return

    // Remove expanded styling from all date cards
    this.dateTrackTarget.querySelectorAll('.date-card').forEach(card => {
      card.classList.remove('border-b-0')
    })

    // Add expanded styling to the selected date card (make it connect to panel below)
    const expandedCard = this.dateTrackTarget.querySelector(`.date-card[data-date="${date}"]`)
    if (expandedCard) {
      expandedCard.classList.add('border-b-0')

      // Match the options panel border color to the card's current state
      const dateData = this.selectedDates.get(date)
      const petIdsForDate = dateData?.petIds || dateData?.existingPetIds || []
      const { isPartial, hasCurrentPet } = this.getPartialSelectionInfo(petIdsForDate, dateData)

      this.updateOptionsPanelBorder(hasCurrentPet, isPartial)
    }

    // Populate time slots
    this.populateTimeSlots(date)

    // Load existing notes for this date
    const dateData = this.selectedDates.get(date)
    if (this.hasNotesTarget) {
      this.notesTarget.value = dateData?.notes || ''
    }

    // Update partial pet warning in the panel
    this.updatePartialWarningInPanel(date)

    // Show panel first, then position the gap (needs panel to be visible for getBoundingClientRect)
    this.optionsPanelTarget.classList.remove('hidden')

    // Position gap after panel is visible - must wait for layout to complete
    if (expandedCard) {
      requestAnimationFrame(() => {
        this.positionTopBorderGap(expandedCard)
      })
    }
  }

  positionTopBorderGap(expandedCard) {
    if (!this.hasTopBorderLeftTarget || !this.hasTopBorderGapTarget || !expandedCard) return

    // Get the position of the expanded card relative to the options panel
    const panelRect = this.optionsPanelTarget.getBoundingClientRect()
    const cardRect = expandedCard.getBoundingClientRect()

    // Calculate the left offset of the card relative to the panel
    const leftOffset = cardRect.left - panelRect.left
    const cardWidth = cardRect.width

    // Set the left border segment width and gap width
    this.topBorderLeftTarget.style.width = `${Math.max(0, leftOffset)}px`
    this.topBorderGapTarget.style.width = `${cardWidth}px`
  }

  updateOptionsPanelBorder(isSelected, isPartial = false) {
    if (!this.hasOptionsPanelContentTarget) return

    const borderClasses = ['border-gray-200', 'border-green-500', 'border-amber-500']

    // Determine which border class to use (always white background for contrast with buttons)
    let borderClass
    if (isSelected) {
      if (isPartial) {
        borderClass = 'border-amber-500'
      } else {
        borderClass = 'border-green-500'
      }
    } else {
      borderClass = 'border-gray-200'
    }

    // Update content area (border only, keep white background for button contrast)
    this.optionsPanelContentTarget.classList.remove(...borderClasses)
    this.optionsPanelContentTarget.classList.add(borderClass)

    // Update top border segments
    if (this.hasTopBorderLeftTarget) {
      this.topBorderLeftTarget.classList.remove(...borderClasses)
      this.topBorderLeftTarget.classList.add(borderClass)
    }
    if (this.hasTopBorderRightTarget) {
      this.topBorderRightTarget.classList.remove(...borderClasses)
      this.topBorderRightTarget.classList.add(borderClass)
    }
  }

  hideOptionsPanel() {
    // Restore bottom border/radius on all date cards
    if (this.hasDateTrackTarget) {
      this.dateTrackTarget.querySelectorAll('.date-card').forEach(card => {
        card.classList.remove('border-b-0')
      })
    }

    this.currentExpandedDate = null
    if (this.hasOptionsPanelTarget) {
      this.optionsPanelTarget.classList.add('hidden')
    }
  }

  populateTimeSlots(date) {
    if (!this.hasTimeSlotsTarget) return

    // Get available times from PTP, or fall back to default if not set
    const availableTimes = this.activity?.available_times || []
    const timeSlots = availableTimes.length > 0
      ? availableTimes.map(time => this.buildTimeSlot(time))
      : this.getDefaultTimeSlots()

    const dateData = this.selectedDates.get(date)

    // Get time slot styling info
    const timeInfo = this.getTimeSlotInfo(dateData)

    const html = timeSlots.map(slot => {
      const info = timeInfo[slot.value] || { selected: false, allPets: false }
      let classes = 'time-slot px-4 py-2 rounded-lg border-2 transition-all flex items-center gap-2 '

      if (info.selected) {
        if (info.allPets) {
          // All selected pets have this time - green
          classes += 'border-green-500 bg-green-50 text-green-700'
        } else {
          // Only some pets have this time - amber/yellow
          classes += 'border-amber-500 bg-amber-50 text-amber-700'
        }
      } else {
        classes += 'border-gray-200 bg-white text-gray-700 hover:border-gray-300'
      }

      return `
        <button type="button"
          class="${classes}"
          data-time="${slot.value}"
          data-date="${date}"
          data-action="click->activity-modal#selectTime">
          <i class="fa-solid ${slot.icon}"></i>
          ${slot.label}
        </button>
      `
    }).join('')

    this.timeSlotsTarget.innerHTML = html
  }

  // Build a time slot object from a PTP time string
  // Handles both legacy format ("am", "noon", "pm") and time format ("09:00")
  buildTimeSlot(timeString) {
    // Handle legacy format first
    const legacySlots = {
      'am': { value: 'am', label: 'Morning (AM)', icon: 'fa-sun' },
      'noon': { value: 'noon', label: 'Midday (Noon)', icon: 'fa-cloud-sun' },
      'pm': { value: 'pm', label: 'Afternoon (PM)', icon: 'fa-moon' }
    }

    if (legacySlots[timeString]) {
      return legacySlots[timeString]
    }

    // Handle HH:MM or HH.MM format
    const separator = timeString.includes(':') ? ':' : '.'
    const hour = parseInt(timeString.split(separator)[0], 10)
    let label, icon

    if (hour < 12) {
      label = `Morning (${this.formatTime(timeString)})`
      icon = 'fa-sun'
    } else if (hour < 14) {
      label = `Midday (${this.formatTime(timeString)})`
      icon = 'fa-cloud-sun'
    } else {
      label = `Afternoon (${this.formatTime(timeString)})`
      icon = 'fa-moon'
    }

    return { value: timeString, label, icon }
  }

  // Format time string for display (e.g., "09:00" -> "9:00 AM")
  // Handles both ":" and "." as separators (PTP uses both formats)
  formatTime(timeString) {
    // Handle both colon and dot separators
    const separator = timeString.includes(':') ? ':' : '.'
    const [hours, minutes] = timeString.split(separator).map(Number)
    const period = hours >= 12 ? 'PM' : 'AM'
    const displayHour = hours % 12 || 12
    return `${displayHour}:${(minutes || 0).toString().padStart(2, '0')} ${period}`
  }

  // Get human-readable label for a time value (for partial warning display)
  getTimeLabel(timeValue) {
    // Handle both PTP format ("09:00") and legacy format ("am", "noon", "pm")
    const legacyLabels = { am: 'Morning', noon: 'Midday', pm: 'Afternoon' }
    if (legacyLabels[timeValue]) {
      return legacyLabels[timeValue]
    }
    // PTP format - parse and return formatted time
    return this.formatTime(timeValue)
  }

  // Default time slots (fallback when no PTP times available)
  getDefaultTimeSlots() {
    return [
      { value: 'am', label: 'Morning (AM)', icon: 'fa-sun' },
      { value: 'noon', label: 'Midday (Noon)', icon: 'fa-cloud-sun' },
      { value: 'pm', label: 'Afternoon (PM)', icon: 'fa-moon' }
    ]
  }

  // Get the first available time for auto-selection
  getFirstAvailableTime() {
    const availableTimes = this.activity?.available_times || []
    return availableTimes.length > 0 ? availableTimes[0] : 'am'
  }

  // Parse YYYY-MM-DD as local date to avoid UTC vs local time issues
  parseLocalDate(dateStr) {
    const [year, month, day] = dateStr.split('-').map(Number)
    return new Date(year, month - 1, day)
  }

  // Filter dates based on day availability (Saturday/Sunday restrictions)
  filterAvailableDates(dates) {
    if (!this.activity) return dates

    return dates.filter(dateStr => {
      const date = this.parseLocalDate(dateStr)
      const dayOfWeek = date.getDay() // 0 = Sunday, 6 = Saturday

      // Check Saturday availability (day 6)
      if (dayOfWeek === 6 && !this.activity.available_saturdays) {
        return false
      }

      // Check Sunday availability (day 0)
      if (dayOfWeek === 0 && !this.activity.available_sundays) {
        return false
      }

      // TODO: Public holiday check would require a list of public holidays
      // For now, we'll rely on the backend to filter these

      return true
    })
  }

  // Get info about each time slot: whether selected and whether all pets have it
  getTimeSlotInfo(dateData) {
    const info = {}

    if (!dateData) return info

    // If no timesByPetId tracking (new selection), all times apply to all selected pets
    if (!dateData.timesByPetId) {
      (dateData.times || []).forEach(time => {
        info[time] = { selected: true, allPets: true }
      })
      return info
    }

    // Build a map of time -> which pets have it
    const timesByTime = {}
    Object.entries(dateData.timesByPetId).forEach(([petId, times]) => {
      if (!this.selectedPets.has(petId)) return
      times.forEach(time => {
        if (!timesByTime[time]) timesByTime[time] = new Set()
        timesByTime[time].add(petId)
      })
    })

    // Determine if each time has all selected pets
    Object.entries(timesByTime).forEach(([time, petIds]) => {
      info[time] = {
        selected: true,
        allPets: petIds.size === this.selectedPets.size
      }
    })

    return info
  }

  // Get times that all currently selected pets have for a date
  getTimesForCurrentPets(dateData) {
    if (!dateData) return []

    // If no timesByPetId tracking (new selection, not from existing), use times directly
    if (!dateData.timesByPetId) {
      return dateData.times || []
    }

    // If only one pet is selected, show only that pet's times
    if (this.selectedPets.size === 1) {
      const selectedPetId = Array.from(this.selectedPets)[0]
      return dateData.timesByPetId[selectedPetId] || []
    }

    // Multiple pets selected - show times that ANY selected pet has
    // (user can see what's selected and add more)
    const allTimes = new Set()
    this.selectedPets.forEach(petId => {
      const petTimes = dateData.timesByPetId[petId] || []
      petTimes.forEach(t => allTimes.add(t))
    })
    return Array.from(allTimes)
  }

  selectTime(event) {
    const time = event.currentTarget.dataset.time
    const date = event.currentTarget.dataset.date
    const button = event.currentTarget

    const dateData = this.selectedDates.get(date) || {}
    // times is now an array of selected times
    let times = dateData.times || []

    // Toggle the time selection
    if (times.includes(time)) {
      times = times.filter(t => t !== time)
    } else {
      times = [...times, time]
    }

    // Auto-select the date when a time is chosen (if any times selected)
    const isSelected = times.length > 0

    // Merge currently selected pets with existing pets for this date
    // This allows adding a new pet to an existing date/time
    const existingPetIds = dateData.petIds || []
    const currentPetIds = Array.from(this.selectedPets)
    const mergedPetIds = [...new Set([...existingPetIds, ...currentPetIds])]

    // Also update timesByPetId to track which pets have which times
    const timesByPetId = { ...(dateData.timesByPetId || {}) }
    currentPetIds.forEach(petId => {
      if (!timesByPetId[petId]) {
        timesByPetId[petId] = []
      }
      if (times.includes(time) && !timesByPetId[petId].includes(time)) {
        timesByPetId[petId] = [...timesByPetId[petId], time]
      } else if (!times.includes(time)) {
        timesByPetId[petId] = timesByPetId[petId].filter(t => t !== time)
      }
    })


    this.selectedDates.set(date, { ...dateData, selected: isSelected, times, petIds: mergedPetIds, timesByPetId })

    // Update date card style
    if (isSelected) {
      this.autoSelectDateCard(date)
    } else {
      const dateCard = this.dateTrackTarget.querySelector(`.date-card[data-date="${date}"]`)
      if (dateCard) {
        this.updateDateCardStyle(dateCard, false)
      }
    }

    // Re-render time slots to show correct colors (green for all pets, amber for partial)
    this.populateTimeSlots(date)

    // Update partial warning in panel
    this.updatePartialWarningInPanel(date)

    // Update options panel border to match the date card state
    if (this.currentExpandedDate === date) {
      const updatedDateData = this.selectedDates.get(date)
      const updatedPetIds = updatedDateData?.petIds || []
      const { isPartial: updatedIsPartial, hasCurrentPet } = this.getPartialSelectionInfo(updatedPetIds, updatedDateData)
      this.updateOptionsPanelBorder(hasCurrentPet, updatedIsPartial)
    }

    this.updateSummary()
    this.autoSave()
  }

  // Auto-select date when options are chosen
  autoSelectDateCard(date) {
    const dateCard = this.dateTrackTarget.querySelector(`.date-card[data-date="${date}"]`)
    if (dateCard) {
      const dateData = this.selectedDates.get(date)
      const petIdsForDate = dateData?.petIds || []
      const { isPartial, missingPets } = this.getPartialSelectionInfo(petIdsForDate, dateData)

      this.updateDateCardStyle(dateCard, true, isPartial)

      if (isPartial && missingPets.length > 0) {
        this.addPartialSelectionWarning(dateCard, petIdsForDate, missingPets)
      } else {
        this.removePartialSelectionWarning(dateCard)
      }
    }
  }

  onNotesInput(event) {
    if (!this.currentExpandedDate) return

    const notes = event.target.value
    const dateData = this.selectedDates.get(this.currentExpandedDate) || {}

    // Only update the notes, preserve the current selection state
    // Don't auto-select the date just because notes were entered
    this.selectedDates.set(this.currentExpandedDate, { ...dateData, notes })

    // Only trigger auto-save and summary update if the date is already selected
    if (dateData.selected) {
      this.updateSummary()
      this.autoSave()
    }
  }

  // Handle activity-level notes input (applies to all sessions)
  onActivityNotesInput(event) {
    this.activityLevelNotes = event.target.value
    if (event.target.value.trim() !== '') {
      this.hideActivityNotesError()
    }
    this.autoSave()
  }

  saveCurrentNotes() {
    if (!this.currentExpandedDate || !this.hasNotesTarget) return

    const dateData = this.selectedDates.get(this.currentExpandedDate) || { selected: true }
    this.selectedDates.set(this.currentExpandedDate, {
      ...dateData,
      notes: this.notesTarget.value
    })
  }

  updateSummary() {
    // Save any notes from current expanded date
    this.saveCurrentNotes()

    // Count total sessions across all selected dates (accounting for per-date pet selections)
    const selectedDatesData = Array.from(this.selectedDates.entries())
      .filter(([_, data]) => data.selected)

    if (selectedDatesData.length === 0) {
      this.updatePriceDisplay(0)
      return
    }

    // Calculate total sessions: sum of (times × pets) for each date
    let totalSessions = 0

    selectedDatesData.forEach(([_, data]) => {
      const times = data.times || []
      const timeCount = times.length > 0 ? times.length : 1
      const petIds = data.petIds && data.petIds.length > 0 ? data.petIds : Array.from(this.selectedPets)
      totalSessions += timeCount * petIds.length
    })

    if (totalSessions === 0) {
      this.updatePriceDisplay(0)
      return
    }

    const totalPrice = totalSessions * this.basePrice
    this.updatePriceDisplay(totalPrice)
  }

  getSelectedDatesCount() {
    return Array.from(this.selectedDates.values()).filter(d => d.selected).length
  }

  updatePriceDisplay(total) {
    if (!this.hasPriceDisplayTarget) return

    if (total > 0) {
      this.priceDisplayTarget.innerHTML = `
        <span class="text-gray-500 text-sm">Total:</span>
        <span class="text-green-600 ml-1">$${total.toFixed(2)}</span>
      `
    } else {
      this.priceDisplayTarget.innerHTML = `
        <span class="text-gray-400 text-sm">Select dates to see price</span>
      `
    }
  }

  updateConfirmButton() {
    // No longer needed - auto-save handles this
  }

  // Build selections array from current state
  buildSelections() {
    const selections = []

    this.selectedDates.forEach((data, date) => {
      if (!data.selected) return

      const petIdsForDate = data.petIds && data.petIds.length > 0
        ? data.petIds
        : Array.from(this.selectedPets)

      // Create a selection for each pet and their specific times
      petIdsForDate.forEach(petId => {
        const pet = this.pets.find(p => p.id === petId)
        const defaultTime = this.getFirstAvailableTime()

        // Get times for this specific pet, or fall back to all times if not tracked
        let timesForPet = data.timesByPetId?.[petId] || data.times || [defaultTime]
        if (timesForPet.length === 0) timesForPet = [defaultTime]

        timesForPet.forEach(time => {
          selections.push({
            activity_id: this.activity.id,
            location_activity_id: this.activity.location_activity_id,
            activity_name: this.activity.title,
            pet_id: petId,
            pet_name: pet?.name,
            date: date,
            time: time,
            notes: data.notes || '',
            price: this.basePrice
          })
        })
      })
    })

    return selections
  }

  // Auto-save selections (debounced)
  autoSave() {
    // Clear any pending auto-save
    if (this.autoSaveTimeout) {
      clearTimeout(this.autoSaveTimeout)
    }

    // Debounce to avoid too many saves
    this.autoSaveTimeout = setTimeout(() => {
      this.saveCurrentNotes()
      const selections = this.buildSelections()

      // Dispatch event for parent controller to handle
      this.dispatch('confirmed', {
        detail: {
          activity: this.activity,
          selections: selections,
          activityNotes: this.activityLevelNotes
        }
      })
    }, 300)
  }

  confirm(event) {
    event.preventDefault()

    // Clear any pending auto-save
    if (this.autoSaveTimeout) {
      clearTimeout(this.autoSaveTimeout)
    }

    // Save immediately
    this.saveCurrentNotes()
    const selections = this.buildSelections()

    // Dispatch event for parent controller to handle
    this.dispatch('confirmed', {
      detail: {
        activity: this.activity,
        selections: selections,
        activityNotes: this.activityLevelNotes
      }
    })

    this.hide()
  }
}
